import { useState, useRef, useCallback } from "react";
import uploadClient from "../../../api/clients/uploadClient";
import axios from "axios";
import {
  getVideoDuration,
  getFileCreationTime,
} from "../utils/fileMetadata";
import {
  scanZipEntries,
  extractZipEntryToBlob,
  streamZipEntryToS3,
  getMimeType,
} from "../utils/zipExtractor";

export interface VideoUploadDetails {
  size: number;
  name: string;
  duration: number;
  recordedAt?: string;
}

export interface UploadJob {
  progress: number;
  file: File;
  evidenceID: string;
  presignedUrl?: string; // Single-upload
  presignedUrls?: string[]; // Multipart
  uploadId?: string; // Multipart
}

export interface FinishedUploadJob {
  fileName: string;
  uploadSuccessful: boolean;
}

interface UseVideoUploadProps {
  fetchVideos: () => void;
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
}

export const buildVideoUploadDetails = async (
  file: File,
): Promise<VideoUploadDetails> => {
  const duration = await getVideoDuration(file);
  const recordedAtMs = await getFileCreationTime(file);
  const details: VideoUploadDetails = {
    size: file.size,
    name: file.name,
    duration,
  };

  if (recordedAtMs !== null) {
    details.recordedAt = new Date(recordedAtMs).toISOString();
  }

  return details;
};

export const useVideoUpload = ({
  fetchVideos,
  showError,
  showSuccess,
}: UseVideoUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const [uploadJobsState, setUploadJobsState] = useState<UploadJob[]>([]);
  const finishedUploadJobs = useRef<FinishedUploadJob[]>([]);
  const uploadJobs = useRef<UploadJob[]>([]);
  const skippedZipFiles = useRef<string[]>([]);

  // We don't hold the fileInputRef here, but we return a reset trigger or just trust the caller to reset?
  // Caller should handle input reset on success/failure if we signal it.

  const completeUploadJob = useCallback(
    (evidenceID: string, uploadSuccessful: boolean) => {
      // Remove upload job from list
      const foundIndex = uploadJobs.current.findIndex(
        (job) => job.evidenceID === evidenceID
      );
      if (foundIndex === -1) return;

      const fileName = uploadJobs.current[foundIndex].file.name;
      const updatedJobs = uploadJobs.current.filter(
        (job) => job.evidenceID !== evidenceID
      );
      uploadJobs.current = updatedJobs;
      setUploadJobsState([...updatedJobs]);

      // Update finished upload jobs
      finishedUploadJobs.current.push({
        fileName: fileName,
        uploadSuccessful: uploadSuccessful,
      });

      // If all uploads are complete
      if (uploadJobs.current.length === 0) {
        const successfulFinishedJobs = finishedUploadJobs.current.filter(
          (job) => job.uploadSuccessful
        );
        if (successfulFinishedJobs.length === 0) {
          showError("All video uploads failed. Please try again later.");
        } else {
          const skipped = skippedZipFiles.current;
          const skippedSuffix =
            skipped.length > 0
              ? ` Skipped unsupported files from zip: ${skipped.join(", ")}.`
              : "";
          showSuccess(
            `The following videos uploaded successfully: ${successfulFinishedJobs
              .map((job) => job.fileName)
              .join(
                ", "
              )}. Please give the uploaded content a few minutes to finish processing before seeing it in a READY state.${skippedSuffix}`
          );
        }
        setUploading(false);
        uploadJobs.current = [];
        finishedUploadJobs.current = [];
        skippedZipFiles.current = [];
        setUploadJobsState([]);
        fetchVideos();
      }
    },
    [fetchVideos, showError, showSuccess]
  );

  const uploadToPresignedUrl = useCallback(
    async (presignedUrl: string, file: File, evidenceID: string) => {
      try {
        await axios.put(presignedUrl, file, {
          headers: {
            "Content-Type": file.type,
            "x-amz-server-side-encryption": "aws:kms",
          },
          onUploadProgress: (progressEvent) => {
            const total = progressEvent.total || 0;
            const current = progressEvent.loaded || 0;
            const percentCompleted = Math.round((current / total) * 100);

            const foundIndex = uploadJobs.current.findIndex(
              (job) => job.evidenceID === evidenceID
            );

            if (foundIndex !== -1 && uploadJobs.current[foundIndex].progress !== percentCompleted) {
              uploadJobs.current[foundIndex].progress = percentCompleted;
              setUploadJobsState([...uploadJobs.current]);
            }
          },
        });
        
        try {
          await uploadClient.put(`/uploadEvidence`, {
            evidenceID,
            uploadSuccessful: true,
          });
          completeUploadJob(evidenceID, true);
        } catch (err) {
          console.error("Failed to confirm upload with server", err);
          showError(
            "Upload succeeded but failed to confirm with server. Please try again."
          );
          completeUploadJob(evidenceID, false);
        }
      } catch (_) {
        try {
          await uploadClient.put(`/uploadEvidence`, {
            evidenceID,
            uploadSuccessful: false,
          });
        } catch (e) {
          console.warn("Failed to notify server of failed upload", e);
        }
        showError("Error uploading video(s). Please try again later.");
        completeUploadJob(evidenceID, false);
      }
    },
    [completeUploadJob, showError]
  );

  const uploadMultipart = useCallback(
    async (
      presignedUrls: string[],
      file: File,
      uploadId: string | undefined,
      evidenceID: string
    ) => {
      const parts = presignedUrls.length;
      if (parts === 0) {
        showError(`No presigned URLs returned for ${file.name}`);
        completeUploadJob(evidenceID, false);
        return;
      }

      const chunkSize = Math.ceil(file.size / parts);
      const partLoaded: number[] = new Array(parts).fill(0);
      const partsInfo: Array<{ partNumber: number; eTag?: string | null }> =
        new Array(parts).fill(null).map((_, i) => ({
          partNumber: i + 1,
          eTag: null,
        }));

      const CONCURRENCY = Math.min(4, navigator?.hardwareConcurrency || 4);
      let currentIndex = 0;

      const uploadPart = async (index: number) => {
        const start = index * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const slice = file.slice(start, end);

        try {
          const resp = await axios.put(presignedUrls[index], slice, {
            headers: {
              "Content-Type": file.type,
            },
            onUploadProgress: (progressEvent) => {
              partLoaded[index] = progressEvent.loaded || 0;
              const totalLoaded = partLoaded.reduce((a, b) => a + b, 0);
              const percentCompleted = Math.round(
                (totalLoaded / file.size) * 100
              );

              const foundIndex = uploadJobs.current.findIndex(
                (job) => job.evidenceID === evidenceID
              );

              if (foundIndex !== -1 && uploadJobs.current[foundIndex].progress !== percentCompleted) {
                uploadJobs.current[foundIndex].progress = percentCompleted;
                setUploadJobsState([...uploadJobs.current]);
              }
            },
          });

          const eTag = resp?.headers?.etag || resp?.headers?.ETag || null;
          partsInfo[index] = { partNumber: index + 1, eTag };
        } catch (err) {
          throw err;
        }
      };

      const workers: Promise<void>[] = new Array(CONCURRENCY)
        .fill(null)
        .map(async () => {
          while (true) {
            let index: number;
            if (currentIndex < parts) {
              index = currentIndex;
              currentIndex += 1;
            } else {
              break;
            }
            await uploadPart(index);
          }
        });

      try {
        await Promise.all(workers);

        const missing = partsInfo.filter((p) => !p.eTag);
        if (missing.length > 0) {
          throw new Error("Missing ETags for multipart upload parts");
        }

        try {
          await uploadClient.put(`/uploadEvidence`, {
            evidenceID,
            uploadSuccessful: true,
            uploadId,
            parts: partsInfo.map((p) => ({
              partNumber: p.partNumber,
              eTag: p.eTag,
            })),
          });
          completeUploadJob(evidenceID, true);
        } catch (err) {
          console.error(
            "Failed to confirm multipart completion with server",
            err
          );
          showError(
            "Upload finished but failed to complete on server. Please try again."
          );
          completeUploadJob(evidenceID, false);
        }
      } catch (error) {
        console.error("Multipart upload failed", error);
        try {
          await uploadClient.put(`/uploadEvidence`, {
            evidenceID,
            uploadSuccessful: false,
            uploadId,
          });
        } catch (e) {
          console.warn("Failed to notify server to abort multipart upload", e);
        }
        showError("Error uploading video(s). Please try again later.");
        completeUploadJob(evidenceID, false);
      }
    },
    [completeUploadJob, showError]
  );

  const uploadZipEntryBlob = useCallback(
    async (
      zipFile: File,
      entryName: string,
      mimeType: string,
      presignedUrl: string,
      fileSize: number,
      evidenceID: string
    ) => {
      try {
        const blob = await extractZipEntryToBlob(zipFile, entryName);
        await axios.put(presignedUrl, blob, {
          headers: {
            "Content-Type": mimeType,
            "x-amz-server-side-encryption": "aws:kms",
          },
          onUploadProgress: (progressEvent) => {
            const total = progressEvent.total || fileSize;
            const current = progressEvent.loaded || 0;
            const percentCompleted = Math.round((current / total) * 100);
            const foundIndex = uploadJobs.current.findIndex(
              (job) => job.evidenceID === evidenceID
            );
            if (foundIndex !== -1 && uploadJobs.current[foundIndex].progress !== percentCompleted) {
              uploadJobs.current[foundIndex].progress = percentCompleted;
              setUploadJobsState([...uploadJobs.current]);
            }
          },
        });
        try {
          await uploadClient.put(`/uploadEvidence`, {
            evidenceID,
            uploadSuccessful: true,
          });
          completeUploadJob(evidenceID, true);
        } catch (err) {
          console.error("Failed to confirm upload with server", err);
          showError(
            "Upload succeeded but failed to confirm with server. Please try again."
          );
          completeUploadJob(evidenceID, false);
        }
      } catch (_) {
        try {
          await uploadClient.put(`/uploadEvidence`, {
            evidenceID,
            uploadSuccessful: false,
          });
        } catch (e) {
          console.warn("Failed to notify server of failed upload", e);
        }
        showError("Error uploading video(s). Please try again later.");
        completeUploadJob(evidenceID, false);
      }
    },
    [completeUploadJob, showError]
  );

  const uploadZipEntryMultipart = useCallback(
    async (
      zipFile: File,
      entryName: string,
      presignedUrls: string[],
      uploadId: string | undefined,
      fileSize: number,
      evidenceID: string
    ) => {
      const parts = presignedUrls.length;
      if (parts === 0) {
        showError(`No presigned URLs returned for ${entryName}`);
        completeUploadJob(evidenceID, false);
        return;
      }
      const partSize = Math.ceil(fileSize / parts);

      try {
        const partsInfo = await streamZipEntryToS3(
          zipFile,
          entryName,
          presignedUrls,
          partSize,
          (loaded, total) => {
            const percentCompleted = Math.round((loaded / total) * 100);
            const foundIndex = uploadJobs.current.findIndex(
              (job) => job.evidenceID === evidenceID
            );
            if (foundIndex !== -1 && uploadJobs.current[foundIndex].progress !== percentCompleted) {
              uploadJobs.current[foundIndex].progress = percentCompleted;
              setUploadJobsState([...uploadJobs.current]);
            }
          }
        );

        const missing = partsInfo.filter((p) => !p.eTag);
        if (missing.length > 0) {
          throw new Error("Missing ETags for multipart upload parts");
        }

        try {
          await uploadClient.put(`/uploadEvidence`, {
            evidenceID,
            uploadSuccessful: true,
            uploadId,
            parts: partsInfo.map((p) => ({
              partNumber: p.partNumber,
              eTag: p.eTag,
            })),
          });
          completeUploadJob(evidenceID, true);
        } catch (err) {
          console.error(
            "Failed to confirm multipart completion with server",
            err
          );
          showError(
            "Upload finished but failed to complete on server. Please try again."
          );
          completeUploadJob(evidenceID, false);
        }
      } catch (error) {
        console.error("Multipart zip upload failed", error);
        try {
          await uploadClient.put(`/uploadEvidence`, {
            evidenceID,
            uploadSuccessful: false,
            uploadId,
          });
        } catch (e) {
          console.warn("Failed to notify server to abort multipart upload", e);
        }
        showError("Error uploading video(s). Please try again later.");
        completeUploadJob(evidenceID, false);
      }
    },
    [completeUploadJob, showError]
  );

  const startZipUpload = useCallback(
    async (zipFile: File): Promise<boolean> => {
      setUploading(true);

      // Step 1: Scan zip entries
      let allEntries: Awaited<ReturnType<typeof scanZipEntries>>;
      try {
        allEntries = await scanZipEntries(zipFile);
      } catch (e) {
        showError("Failed to read zip file. The file may be corrupted.");
        setUploading(false);
        return false;
      }

      const validEntries = allEntries.filter((e) => e.valid);
      const skippedNames = allEntries.filter((e) => !e.valid).map((e) => e.name);

      if (validEntries.length === 0) {
        showError(
          "The zip file contains no supported video files (.mp4, .mov, .mp3)."
        );
        setUploading(false);
        return false;
      }

      skippedZipFiles.current = skippedNames;

      // Step 2: Probe metadata for each valid entry (serially to limit memory pressure)
      const videos: VideoUploadDetails[] = [];
      const errors: string[] = [];

      for (const entry of validEntries) {
        try {
          const blob = await extractZipEntryToBlob(zipFile, entry.name);
          const syntheticFile = new File([blob], entry.name, {
            type: getMimeType(entry.name),
          });
          const duration = await getVideoDuration(syntheticFile);
          const recordedAtMs = await getFileCreationTime(syntheticFile);
          const details: VideoUploadDetails = {
            size: blob.size,
            name: entry.name,
            duration,
          };
          if (recordedAtMs !== null) {
            details.recordedAt = new Date(recordedAtMs).toISOString();
          }
          videos.push(details);
        } catch (error) {
          const errorMessage = `Cannot process file "${entry.name}" from zip.`;
          console.error(errorMessage, error);
          errors.push(errorMessage);
        }
      }

      if (errors.length > 0) {
        showError(
          "Unable to upload files due to verification errors: " +
            errors.join(" ")
        );
        setUploading(false);
        return false;
      }

      // Step 3: Request presigned URLs and start uploads
      try {
        const response = await uploadClient.post(`/uploadEvidence`, { videos });
        fetchVideos();

        for (const details of videos) {
          const entry = response.data.presignedUrls[details.name];
          if (!entry) {
            showError(`No presigned URL returned for file ${details.name}`);
            const missingMimeType = getMimeType(details.name);
            const missingFile = new File([], details.name, { type: missingMimeType });
            const missingID = `no-entry:${details.name}`;
            uploadJobs.current.push({ progress: 0, file: missingFile, evidenceID: missingID });
            completeUploadJob(missingID, false);
            continue;
          }

          const evidenceID = entry.evidenceID;
          const mimeType = getMimeType(details.name);
          // Synthetic File for tracking/display — actual bytes come from fflate stream
          const syntheticFile = new File([], details.name, { type: mimeType });

          if (entry.presignedUrl) {
            uploadJobs.current.push({
              progress: 0,
              file: syntheticFile,
              evidenceID,
              presignedUrl: entry.presignedUrl,
            });
            uploadZipEntryBlob(
              zipFile,
              details.name,
              mimeType,
              entry.presignedUrl,
              details.size,
              evidenceID
            );
          } else if (entry.presignedUrls && entry.presignedUrls.length > 0) {
            uploadJobs.current.push({
              progress: 0,
              file: syntheticFile,
              evidenceID,
              presignedUrls: entry.presignedUrls,
              uploadId: entry.uploadId,
            });
            uploadZipEntryMultipart(
              zipFile,
              details.name,
              entry.presignedUrls,
              entry.uploadId,
              details.size,
              evidenceID
            );
          } else {
            showError(`No presigned URL returned for file ${details.name}`);
            uploadJobs.current.push({ progress: 0, file: syntheticFile, evidenceID });
            completeUploadJob(evidenceID, false);
          }
        }

        setUploadJobsState([...uploadJobs.current]);
        console.log("Started zip upload for entries", uploadJobs.current);
      } catch (error) {
        console.error("Error uploading zip contents:", error);
        const errorMessage =
          (error as any)?.response?.data?.error || "Please try again later.";
        showError("Error uploading zip contents. " + errorMessage);
        setUploading(false);
        uploadJobs.current = [];
      }

      return true;
    },
    [
      fetchVideos,
      showError,
      uploadZipEntryBlob,
      uploadZipEntryMultipart,
      completeUploadJob,
    ]
  );

  const startUpload = useCallback(
    async (files: FileList) => {
      // Route single zip file through the zip upload path
      if (
        files.length === 1 &&
        files[0].name.toLowerCase().endsWith(".zip")
      ) {
        return startZipUpload(files[0]);
      }

      setUploading(true);
      const videos: VideoUploadDetails[] = [];
      const errors = [];

      // Metadata extraction
      for (let i = 0; i < files.length; i++) {
        try {
          videos.push(await buildVideoUploadDetails(files[i]));
        } catch (error) {
          const errorMessage = `Cannot start upload of file ${files[i].name}, file is unable to be processed.`;
          console.error(errorMessage, error);
          errors.push(errorMessage);
        }
      }

      if (errors.length > 0) {
        showError(
          "Unable to upload files due to verification errors: " +
            errors.join(" ")
        );
        setUploading(false);
        return false; // Signal failure/reset
      }

      // API Call
      try {
        const response = await uploadClient.post(`/uploadEvidence`, { videos });
        fetchVideos();

        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const entry = response.data.presignedUrls[file.name];

          if (!entry) {
            showError(`No presigned URL returned for file ${file.name}`);
            const missingID = `no-entry:${file.name}`;
            uploadJobs.current.push({ progress: 0, file: file, evidenceID: missingID });
            completeUploadJob(missingID, false);
            continue;
          }

          const evidenceID = entry.evidenceID;

          if (entry.presignedUrl) {
            uploadJobs.current.push({
              progress: 0,
              file: file,
              evidenceID: evidenceID,
              presignedUrl: entry.presignedUrl,
            });
            uploadToPresignedUrl(entry.presignedUrl, file, evidenceID);
          } else if (entry.presignedUrls && entry.presignedUrls.length > 0) {
            uploadJobs.current.push({
              progress: 0,
              file: file,
              evidenceID: evidenceID,
              presignedUrls: entry.presignedUrls,
              uploadId: entry.uploadId,
            });
            uploadMultipart(
              entry.presignedUrls,
              file,
              entry.uploadId,
              evidenceID
            );
          } else {
            showError(`No presigned URL returned for file ${file.name}`);
            uploadJobs.current.push({ progress: 0, file: file, evidenceID: evidenceID });
            completeUploadJob(evidenceID, false);
          }
        }
        setUploadJobsState(uploadJobs.current);
        console.log("Started upload for files", uploadJobs.current);
      } catch (error) {
        console.error("Error uploading video:", error);
        const errorMessage =
          (error as any)?.response?.data?.error || "Please try again later.";
        showError("Error uploading video. " + errorMessage);
        setUploading(false);
        uploadJobs.current = [];
      }
      
      return true; // Started successfully
    },
    [fetchVideos, showError, uploadMultipart, uploadToPresignedUrl, completeUploadJob, startZipUpload]
  );

  return {
    uploading,
    uploadJobs: uploadJobsState,
    startUpload,
  };
};
